import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/Button/Button';
import { FormInput } from '../../../ui/FormInput/FormInput';
import { ValidationMessage } from '../../../components/Forms/ValidationMessage';
import { relanceFollowUpDaysService } from '../../../services/relanceFollowUpDaysService';
import { TranslationKey } from '../../../i18n/translationKeys';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const MAX_DAYS = 365;
const DAYS_INPUT = /^\d{0,3}$/;

const parseDays = (value: string): number | null => {
    if (value === '') return null;
    const days = Number(value);
    return days <= MAX_DAYS ? days : null;
};

export const RelanceFollowUpDaysPanel: React.FC = () => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [value, setValue] = useState('');
    const [saveState, setSaveState] = useState<SaveState>('idle');
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const setting = await relanceFollowUpDaysService.get();
                if (cancelled) return;
                setValue(String(setting.followUpDays));
                setLoaded(true);
            } catch (e) {
                console.error('Failed to load the relance follow-up delay:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!loaded) return null;

    const handleSave = async () => {
        const days = parseDays(value);
        setInvalid(days === null);
        if (days === null) return;

        setSaveState('saving');
        try {
            await relanceFollowUpDaysService.set(days);
            setSaveState('saved');
        } catch (e) {
            setSaveState('error');
            console.error('Failed to save the relance follow-up delay:', e);
        }
    };

    return (
        <div className="space-y-2">
            <h2 className="font-medium text-primary-1100">{t(TranslationKey.Settings.RelanceFollowUpDays)}</h2>
            <p className="text-primary-700">{t(TranslationKey.Settings.RelanceFollowUpDaysDescription)}</p>
            <div className="flex items-start gap-2 pt-1">
                <div>
                    <div className="flex items-center gap-2">
                        <FormInput
                            id="settings-relance-follow-up-days"
                            data-testid="settings-relance-follow-up-days"
                            className="w-24"
                            type="text"
                            inputMode="numeric"
                            value={value}
                            onChange={(e) => {
                                if (!DAYS_INPUT.test(e.target.value)) return;
                                setValue(e.target.value);
                                setSaveState('idle');
                                setInvalid(false);
                            }}
                            aria-label={t(TranslationKey.Settings.RelanceFollowUpDays)}
                            aria-invalid={invalid}
                            aria-describedby={invalid ? 'settings-relance-follow-up-days-err' : undefined}
                        />
                        <span data-testid="settings-relance-follow-up-days-unit" className="text-primary-700">
                            {t(TranslationKey.Settings.RelanceFollowUpDaysUnit)}
                        </span>
                    </div>
                    <ValidationMessage
                        id="settings-relance-follow-up-days-err"
                        data-testid="settings-relance-follow-up-days-invalid"
                        message={invalid ? t(TranslationKey.Settings.RelanceFollowUpDaysInvalid, { max: MAX_DAYS }) : undefined}
                    />
                </div>
                <Button data-testid="settings-relance-follow-up-days-save" onClick={() => void handleSave()} loading={saveState === 'saving'}>
                    {t(TranslationKey.Settings.RelanceFollowUpDaysSave)}
                </Button>
            </div>
            {saveState === 'saved' && (
                <p data-testid="settings-relance-follow-up-days-saved" className="text-primary-700 text-s">{t(TranslationKey.Settings.RelanceFollowUpDaysSaved)}</p>
            )}
            {saveState === 'error' && (
                <p data-testid="settings-relance-follow-up-days-error" className="text-danger text-s">{t(TranslationKey.Settings.RelanceFollowUpDaysError)}</p>
            )}
        </div>
    );
};
